/* --- File: src/App.js ---
   [TỐI ƯU TỐC ĐỘ] Mọi trang (trừ LoginPage — trang đầu tiên ai cũng vào) đều
   dùng React.lazy() — JS của từng trang chỉ tải khi người dùng THỰC SỰ vào
   trang đó, thay vì tải hết 1 lần lúc mở web. Giúp lần tải đầu tiên nhanh hơn
   nhiều, đặc biệt trên mạng chậm/máy yếu.

   [UPGRADED] Sử dụng service layer cho Firebase operations
*/
import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/UserContext';
import { App as AntApp, Spin } from 'antd';
import { ClassProvider } from './context/ClassContext';
import { ExamProvider } from './context/ExamContext';

// Trang đăng nhập — tải ngay (là trang đầu tiên hầu hết mọi người sẽ thấy)
import LoginPage from './pages/LoginPage';
import MainLayout from './layouts/MainLayout';
import ProtectedRoute from './components/ProtectedRoute';

// Toàn bộ các trang còn lại — tải theo yêu cầu (lazy)
const DashboardPage             = lazy(() => import('./pages/DashboardPage'));
const UserManagementPage        = lazy(() => import('./pages/UserManagementPage'));
const DatabaseAdminPage         = lazy(() => import('./pages/DatabaseAdminPage'));
const RiskAnalysisPage          = lazy(() => import('./pages/RiskAnalysisPage'));
const QuestionBankPage          = lazy(() => import('./pages/QuestionBankPage'));
const SubjectManagementPage     = lazy(() => import('./pages/SubjectManagementPage'));
const ClassManagementPage       = lazy(() => import('./pages/ClassManagementPage'));
const LessonsPage               = lazy(() => import('./pages/LessonsPage'));
const StudentDashboard          = lazy(() => import('./pages/StudentDashboard'));
const ReportManagementPage      = lazy(() => import('./pages/ReportManagementPage'));
const AssignmentPage            = lazy(() => import('./pages/AssignmentPage'));
const ExamManagementPage        = lazy(() => import('./pages/ExamManagementPage'));
const ExamRoomPage              = lazy(() => import('./pages/ExamRoomPage'));
const GradebookPage             = lazy(() => import('./pages/GradebookPage'));
const StudentProfilePage        = lazy(() => import('./pages/StudentProfilePage'));
const PCAPInfoPage              = lazy(() => import('./pages/PCAPInfoPage'));
const PCAPPracticePage          = lazy(() => import('./pages/PCAPPracticePage'));
const PCAPTheoryHubPage         = lazy(() => import('./pages/PCAPTheoryHubPage'));
const PCAPContentAdminPage      = lazy(() => import('./pages/PCAPContentAdminPage'));
const SyllabusPage              = lazy(() => import('./pages/SyllabusPage'));
const SchedulePage              = lazy(() => import('./pages/SchedulePage'));
const NotFoundPage              = lazy(() => import('./pages/NotFoundPage'));
const ProfilePage               = lazy(() => import('./pages/ProfilePage'));
const LessonManagementPage      = lazy(() => import('./pages/LessonManagementPage'));
const CapstoneProjectPage       = lazy(() => import('./pages/CapstoneProjectPage'));
const PublicVotePage            = lazy(() => import('./pages/PublicVotePage'));
const PublicReportPage          = lazy(() => import('./pages/PublicReportPage'));
const PublicCertPage            = lazy(() => import('./pages/PublicCertPage'));
const PortfolioPage             = lazy(() => import('./pages/PortfolioPage'));
const MyCertificatesPage        = lazy(() => import('./pages/MyCertificatesPage'));
const CertificateManagementPage = lazy(() => import('./pages/CertificateManagementPage'));
// Student Care — bảng case tổng hợp kiểu Odoo (GV/Admin) + trang gửi yêu cầu
// hỗ trợ & theo dõi của học sinh. Thay thế RiskInterventionPage (3 cột cũ).
const CaseListPage              = lazy(() => import('./pages/CaseListPage'));
const CaseCreatePage            = lazy(() => import('./pages/CaseCreatePage'));
const CaseDetailPage            = lazy(() => import('./pages/CaseDetailPage'));
const StudentSupportPage        = lazy(() => import('./pages/StudentSupportPage'));
const VisitAnalyticsPage        = lazy(() => import('./pages/VisitAnalyticsPage'));

// Màn hình chờ ngắn khi 1 trang đang tải lần đầu — thay cho màn hình trắng
const PageLoadingFallback = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#f8fafc' }}>
        <Spin size="large" />
    </div>
);

// Resolve learner entry points before downloading staff management screens.
function LearningRoute({ tab, children }) {
    const { hasRole } = useAuth();
    const location = useLocation();
    if (hasRole(['student', 'parent']) && !hasRole(['admin', 'teacher', 'coordinator'])) {
        const params = new URLSearchParams(location.search);
        params.set('tab', tab);
        ['grade', 'gradeExam', 'edit', 'viewScore'].forEach(key => params.delete(key));
        return <Navigate to={`/student-dashboard?${params}`} replace />;
    }
    return children;
}

function App() {
    return (
        <AntApp>
            {/* Context providers for global state */}
            <ClassProvider>
                <ExamProvider>
                    <Suspense fallback={<PageLoadingFallback />}>
                        <Routes>

                            {/* 1. ROUTE CÔNG KHAI - KHÔNG CẦN ĐĂNG NHẬP */}
                            <Route path="/login" element={<LoginPage />} />
                            <Route path="/public-vote" element={<PublicVotePage />} />
                            <Route path="/p/:shareId" element={<PublicReportPage />} />
                            <Route path="/p/cert/:certCode" element={<PublicCertPage />} />
                            <Route path="/portfolio/:studentId" element={<PortfolioPage />} />

                            {/* 2. ROUTE CẦN ĐĂNG NHẬP RIÊNG BIỆT (PHÒNG THI) */}
                            <Route
                                path="/exam-room/:examId"
                                element={
                                    <ProtectedRoute>
                                        <ExamRoomPage />
                                    </ProtectedRoute>
                                }
                            />

                            {/* 3. TOÀN BỘ HỆ THỐNG QUẢN LÝ (CẦN ĐĂNG NHẬP & CÓ LAYOUT CHÍNH) */}
                            <Route
                                path="/"
                                element={
                                    <ProtectedRoute>
                                        <MainLayout />
                                    </ProtectedRoute>
                                }
                            >
                                <Route index element={<DashboardPage />} />
                                <Route path="users" element={<UserManagementPage />} />
                                <Route path="database-admin" element={<DatabaseAdminPage />} />
                                <Route path="risk-analysis" element={<RiskAnalysisPage />} />
                                <Route path="question-bank" element={<QuestionBankPage />} />
                                <Route path="subjects" element={<SubjectManagementPage />} />
                                <Route path="classes" element={<ClassManagementPage />} />
                                <Route path="lessons" element={<LessonsPage />} />
                                <Route path="student-dashboard" element={<StudentDashboard />} />
                                <Route path="reports" element={<LearningRoute tab="reports"><ReportManagementPage /></LearningRoute>} />
                                <Route path="assignments" element={<LearningRoute tab="assignments"><AssignmentPage /></LearningRoute>} />
                                <Route path="exams" element={<LearningRoute tab="exams"><ExamManagementPage /></LearningRoute>} />
                                <Route path="gradebook" element={<GradebookPage />} />
                                <Route path="student-profile" element={<StudentProfilePage />} />
                                <Route path="pcap" element={<PCAPInfoPage />} />
                                <Route path="pcap-practice" element={<PCAPPracticePage />} />
                                <Route path="pcap-theory" element={<PCAPTheoryHubPage />} />
                                <Route path="pcap-content-admin" element={<PCAPContentAdminPage />} />
                                <Route path="syllabus" element={<SyllabusPage />} />
                                <Route path="schedule" element={<SchedulePage />} />
                                <Route path="learning-materials" element={<LessonManagementPage />} />
                                <Route path="profile" element={<ProfilePage />} />
                                <Route path="my-certificates" element={<MyCertificatesPage />} />
                                <Route path="certificate-management" element={<CertificateManagementPage />} />
                                <Route path="projects" element={<CapstoneProjectPage isTabMode={false} />} />

                                {/* Student Care — bảng case tổng hợp (GV/Admin) */}
                                {/* legacy route giữ để không break bookmark cũ */}
                                <Route path="risk-intervention" element={<CaseListPage />} />
                                <Route path="interventions" element={<CaseListPage />} />
                                <Route path="interventions/new" element={<CaseCreatePage />} />
                                <Route path="interventions/:caseId" element={<CaseDetailPage />} />

                                {/* Student Care — phía học sinh: xem case + gửi yêu cầu hỗ trợ */}
                                <Route path="student-care" element={<StudentSupportPage />} />

                                {/* Báo cáo lượt truy cập web (admin/coordinator) */}
                                <Route path="visit-analytics" element={<VisitAnalyticsPage />} />
                            </Route>

                            {/* 4. TRANG LỖI 404 */}
                            <Route path="*" element={<NotFoundPage />} />

                        </Routes>
                    </Suspense>
                </ExamProvider>
            </ClassProvider>
        </AntApp>
    );
}

export default App;